'use client';

import React, { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import {
  faCalendarRange,
  faCaretDown,
  faCaretUp,
  faCheck,
} from '@fortawesome/pro-solid-svg-icons';
import useTableFilterSelections from 'src/hooks/filters/useTableFilterSelections';
import { MIN_VOLUME_YEAR, MAX_VOLUME_YEAR } from 'src/components/constants';

interface YearRangeDropdownProps {
  location?: string;
}

const YearRangeDropdown: React.FC<YearRangeDropdownProps> = ({
  location = 'lists',
}) => {
  const MIN_YEAR = MIN_VOLUME_YEAR;
  const MAX_YEAR = MAX_VOLUME_YEAR;

  const { date_range, setDateRange } = useTableFilterSelections(location);
  const [isOpen, setIsOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<'start' | 'end' | null>(
    null
  );
  const [startYear, setStartYear] = useState(
    parseInt(date_range.gte) || MIN_YEAR
  );
  const [endYear, setEndYear] = useState(parseInt(date_range.lte) || MAX_YEAR);

  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Generate array of years from MIN_YEAR to MAX_YEAR
  const years = Array.from(
    { length: MAX_YEAR - MIN_YEAR + 1 },
    (_, i) => MIN_YEAR + i
  );

  // Update local state when Redux state changes
  useEffect(() => {
    if (date_range) {
      setStartYear(parseInt(date_range.gte) || MIN_YEAR);
      setEndYear(parseInt(date_range.lte) || MAX_YEAR);
    }
  }, [date_range]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setActiveDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      setActiveDropdown(null);
    }
  };

  const toggleYearDropdown = (type: 'start' | 'end') => {
    if (activeDropdown === type) {
      setActiveDropdown(null);
    } else {
      setActiveDropdown(type);
    }
  };

  const handleYearClick = (type: 'start' | 'end', year: number) => {
    let newStartYear = startYear;
    let newEndYear = endYear;

    if (type === 'start') {
      if (year > endYear) {
        newStartYear = year;
        newEndYear = year;
        setStartYear(year);
        setEndYear(year);
      } else {
        newStartYear = year;
        setStartYear(year);
      }
    } else {
      if (year < startYear) {
        newStartYear = year;
        newEndYear = year;
        setStartYear(year);
        setEndYear(year);
      } else {
        newEndYear = year;
        setEndYear(year);
      }
    }

    // Update temporary state in Redux (this will be applied when Apply button is clicked)
    setDateRange({
      gte: newStartYear.toString(),
      lte: newEndYear.toString(),
    });

    // Only close the specific year dropdown, not the main dropdown
    setActiveDropdown(null);
  };

  return (
    <div
      data-has-value="Yes"
      data-hint="false"
      data-label="true"
      data-show-operator="true"
      data-size="Md"
      data-state="Default"
      data-type="Icon"
      className="h-10 flex-1 inline-flex flex-col justify-start items-start gap-1 relative"
      ref={containerRef}
    >
      <div className="self-stretch justify-start text-primary text-xs font-semibold font-['Inter'] leading-none">
        Year Range
      </div>
      <div
        className="self-stretch px-4 py-2 bg-white rounded-full outline outline-1 -outline-offset-1 outline-neutral-200 inline-flex justify-between items-center cursor-pointer"
        onClick={toggleDropdown}
      >
        <div className="flex justify-start items-center gap-2">
          <Icon icon={faCalendarRange} className="text-neutral-600 w-5 h-5" />
          <div className="justify-start text-primary text-base font-normal font-['Inter'] leading-normal">
            {startYear}-{endYear}
          </div>
        </div>
        <Icon icon={faCaretDown} className="text-neutral-500 w-5 h-5" />
      </div>

      {/* Main Dropdown - Positioned BELOW the trigger */}
      {isOpen && (
        <div
          ref={dropdownRef}
          className="absolute top-full left-0 mt-5 z-[9999] w-full bg-white rounded-xl shadow-lg overflow-visible border border-neutral-200"
        >
          <div className="p-4 flex gap-4">
            {/* Start Year */}
            <div className="flex flex-col gap-1 relative w-full">
              <label className="text-primary text-xs font-semibold">
                Start Year
              </label>
              <div
                className="w-full px-4 py-1.5 border border-neutral-300 rounded-full flex justify-between items-center cursor-pointer"
                onClick={() => toggleYearDropdown('start')}
              >
                <span>{startYear}</span>
                <Icon
                  icon={activeDropdown === 'start' ? faCaretUp : faCaretDown}
                  className="text-neutral-500 w-4 h-4"
                />
              </div>

              {/* Start Year Dropdown - Positioned BELOW the year field */}
              {activeDropdown === 'start' && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-neutral-200 rounded-lg shadow-lg z-[10000] max-h-48 overflow-y-auto">
                  {years.map((year) => (
                    <div
                      key={`start-${year}`}
                      className={`p-2 cursor-pointer flex justify-between items-center ${
                        year === startYear
                          ? 'bg-sky-50 text-sky-600 font-medium'
                          : 'hover:bg-neutral-50'
                      }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleYearClick('start', year);
                      }}
                    >
                      {year}
                      {year === startYear && (
                        <Icon icon={faCheck} className="text-sky-600 w-4 h-4" />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* End Year */}
            <div className="flex flex-col gap-1 relative w-full">
              <label className="text-primary text-xs font-semibold">
                End Year
              </label>
              <div
                className="w-full px-4 py-1.5 border border-neutral-300 rounded-full flex justify-between items-center cursor-pointer"
                onClick={() => toggleYearDropdown('end')}
              >
                <span>{endYear}</span>
                <Icon
                  icon={activeDropdown === 'end' ? faCaretUp : faCaretDown}
                  className="text-neutral-500 w-4 h-4"
                />
              </div>

              {/* End Year Dropdown */}
              {activeDropdown === 'end' && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-neutral-200 rounded-lg shadow-lg z-[10000] max-h-48 overflow-y-auto">
                  {years.map((year) => (
                    <div
                      key={`end-${year}`}
                      className={`p-2 cursor-pointer flex justify-between items-center ${
                        year === endYear
                          ? 'bg-sky-50 text-sky-600 font-medium'
                          : 'hover:bg-neutral-50'
                      }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleYearClick('end', year);
                      }}
                    >
                      {year}
                      {year === endYear && (
                        <Icon icon={faCheck} className="text-sky-600 w-4 h-4" />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="self-stretch h-0 outline outline-1 outline-offset-[-0.50px] outline-neutral-200"></div>
        </div>
      )}
    </div>
  );
};

export default YearRangeDropdown;
